import React from 'react';
import { useTranslation } from 'react-i18next';
import { StudySession } from '../../services/api';
import { ArrowLeft, Brain, Calendar, CheckCircle } from 'lucide-react';
import { Card, SpaceCard } from '../ui/Card';
import { Button } from '../ui/Button';

interface StudySessionLayoutProps {
  session: StudySession;
  courseSessions: StudySession[];
  getSessionPosition: () => { current: number; total: number } | null;
  isRTL: boolean;
  onNavigateBack: () => void;
  children: React.ReactNode;
}

const StudySessionLayout: React.FC<StudySessionLayoutProps> = ({
  session,
  courseSessions,
  getSessionPosition,
  isRTL,
  onNavigateBack,
  children
}) => {
  const { t } = useTranslation();
  const sessionPosition = getSessionPosition();

  return (
    <div className="min-h-screen py-4 md:py-8 relative">
      {/* Header Section */}
      <div className="max-w-6xl mx-auto mb-4 md:mb-6 px-3 md:px-4">
        {/* Breadcrumb Navigation */}
        <div className="mb-3 md:mb-4">
          <Button
            onClick={onNavigateBack}
            variant="ghost"
            className={`flex items-center text-bg-secondary hover:text-vibrant-orange transition-colors group text-sm md:text-base ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}
          >
            <ArrowLeft className="w-3 h-3 md:w-4 md:h-4 group-hover:-translate-x-1 transition-transform duration-200 flex-shrink-0" />
            <span className="font-medium truncate max-w-[200px] md:max-w-none">
              {t('sessions:flow.navigation.backToCourse', { 
                courseName: session.course?.name || t('common:labels.course') 
              })}
            </span>
          </Button>
        </div>

        {/* Main Header Card */}
        <SpaceCard className="overflow-hidden">
          <div className="p-3 md:p-5">
            <div className={`flex items-start ${isRTL ? 'justify-start' : 'justify-between'}`}>
              {/* Session Info */}
              <div className={`flex items-start flex-1 ${isRTL ? 'space-x-reverse space-x-3 md:space-x-4' : 'space-x-3 md:space-x-4'}`}>
                {/* Session Icon */}
                <div className={`flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-vibrant-orange to-vibrant-orange/90 rounded-lg md:rounded-xl shadow-lg flex-shrink-0 ${isRTL ? 'order-2' : 'order-1'}`}>
                  <Brain className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                
                {/* Session Details */}
                <div className={`flex-1 min-w-0 ${isRTL ? 'order-1 text-right pr-1 md:pr-2' : 'order-2'}`}>
                  {/* Course Context */}
                  {sessionPosition && (
                    <div className={`mb-1 md:mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                      <div className={`flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 ${isRTL ? 'sm:flex-row-reverse sm:justify-start' : 'sm:justify-start'}`}>
                        <div className="px-2 py-0.5 md:px-2.5 bg-muted-purple/20 text-muted-purple rounded-full text-xs font-medium w-fit">
                          {t('sessions:flow.navigation.sessionOf', { 
                            current: sessionPosition.current, 
                            total: sessionPosition.total 
                          })}
                        </div>
                        {session.course?.name && (
                          <span className="text-surface-muted text-xs truncate">
                            {t('sessions:flow.navigation.inCourse', { courseName: session.course.name })}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Session Title */}
                  <h1 className={`text-lg md:text-2xl font-bold text-surface-primary mb-1 md:mb-2 leading-tight ${isRTL ? 'text-right' : ''}`}>
                    {session.title}
                  </h1>
                  
                  {/* Session Meta */}
                  <div className={`flex flex-col sm:flex-row sm:items-center text-xs text-surface-muted gap-1 sm:gap-3 ${isRTL ? 'sm:flex-row-reverse sm:justify-start' : 'sm:justify-start'}`}>
                    {/* Completed Date - Show first in RTL, last in LTR */}
                    {session.completed_at && (
                      <div className={`flex items-center gap-1.5 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                        <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                        <span className="truncate">
                          {t('sessions:flow.navigation.completedOn')} {new Date(session.completed_at).toLocaleDateString(isRTL ? 'he-IL' : 'en-US')}
                        </span>
                      </div>
                    )}
                    
                    {/* Created Date - Show last in RTL, first in LTR */}
                    <div className={`flex items-center gap-1.5 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                      <Calendar className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">
                        {t('sessions:flow.navigation.createdOn')} {new Date(session.created_at).toLocaleDateString(isRTL ? 'he-IL' : 'en-US')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </SpaceCard>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-3 md:px-4">
        {children}
      </div>
    </div>
  );
};

export default StudySessionLayout; 